<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Container, InputV2, Text, ToggleV2 } from '@Shelly/shelly-design-system';
	import {
		capFirstChar,
		convertTime,
		getErrorOrInfoMessageForMinAndMax,
		getErrorOrInfoMessageForMinAndMaxUnit,
	} from 'src/helpers/utilities';
	import { getNumberInputConfig } from 'src/helpers/number_input_helpers';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import { callBluTRV, getTrvComponentByDevice } from 'src/helpers/virtual_helpers/blutrv_helpers';
	import type { Device } from 'src/types/Device';

	export let device: Device;

	const durationMin = 0;
	const durationMax = 35791394;
	const targetMin = 4;
	const targetMax = 30;

	const config = getTrvComponentByDevice(device)?.config?.['trv:0'];

	let boostDuration: number | string = convertTime(config?.default_boost_duration ?? 0, 'seconds');
	let overrideEnable = config?.override_enable ?? false;
	let overrideTarget: number | string = config?.default_override_target_C ?? targetMin;
	let overrideDuration: number | string = convertTime(
		config?.default_override_duration ?? 0,
		'seconds',
	);
	let waitForResponse = false;

	let lastSavedBoostDuration = boostDuration;
	let lastSavedOverrideEnable = overrideEnable;
	let lastSavedOverrideTarget = overrideTarget;
	let lastSavedOverrideDuration = overrideDuration;

	$: numericBoostDuration = Number(boostDuration);
	$: numericOverrideDuration = Number(overrideDuration);
	$: numericOverrideTarget = Number(overrideTarget);
	$: boostDurationInfo = getErrorOrInfoMessageForMinAndMax(
		numericBoostDuration,
		durationMin,
		durationMax,
		false,
		true,
	);
	$: overrideDurationInfo = getErrorOrInfoMessageForMinAndMax(
		numericOverrideDuration,
		durationMin,
		durationMax,
		false,
		true,
	);
	$: overrideTargetInfo = getErrorOrInfoMessageForMinAndMaxUnit(
		numericOverrideTarget,
		targetMin,
		targetMax,
		'°C',
	);

	$: boostDurationDirty = numericBoostDuration !== Number(lastSavedBoostDuration);
	$: overrideEnableDirty = overrideEnable !== lastSavedOverrideEnable;
	$: overrideTargetDirty = numericOverrideTarget !== Number(lastSavedOverrideTarget);
	$: overrideDurationDirty = numericOverrideDuration !== Number(lastSavedOverrideDuration);
	$: isDirty =
		boostDurationDirty || overrideEnableDirty || overrideTargetDirty || overrideDurationDirty;
	$: hasError = !!(boostDurationInfo.error || overrideDurationInfo.error || overrideTargetInfo.error);
	$: saveDisabled = waitForResponse || !isDirty || hasError;

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		setStickyBottomAction({
			onClick: save,
			loading: waitForResponse,
			disabled: saveDisabled,
			icon: 'fas fa-save',
		});
	}

	const save = async () => {
		if (saveDisabled) return;
		if (boostDuration === '') boostDuration = 0;
		if (overrideDuration === '') overrideDuration = 0;

		const payload: Record<string, number | boolean> = {};
		if (boostDurationDirty) {
			payload.default_boost_duration = convertTime(numericBoostDuration, 'minutes');
		}
		if (overrideEnableDirty) payload.override_enable = overrideEnable;
		if (overrideTargetDirty) payload.default_override_target_C = numericOverrideTarget;
		if (overrideDurationDirty) {
			payload.default_override_duration = convertTime(numericOverrideDuration, 'minutes');
		}
		if (Object.keys(payload).length === 0) return;

		waitForResponse = true;
		const res = await callBluTRV(device, 'Trv.Setconfig', {
			id: 0,
			config: payload,
		});
		if (res?.isok) {
			if (boostDurationDirty) lastSavedBoostDuration = numericBoostDuration;
			if (overrideEnableDirty) lastSavedOverrideEnable = overrideEnable;
			if (overrideTargetDirty) lastSavedOverrideTarget = numericOverrideTarget;
			if (overrideDurationDirty) lastSavedOverrideDuration = numericOverrideDuration;
		}
		waitForResponse = false;
	};
</script>

<div class="blu-trv-boost-settings">
	<Container padding="s" type="borderSolid" backgroundColor="color-base">
		<Text styleType="h2" class="mb-s">
			{capFirstChar(
				$format('sub_settings_title_trv_boost_duration_override', {
					default: 'Boost duration override',
				}),
			)}
		</Text>
		<Text styleType="body-small" class="mb-s">
			{$format('text_trv_setting_duration', { default: 'Duration' })}
		</Text>
		<InputV2
			aria-label={$format('input_blu_trv_boost_duration', {
				default: 'Enter the boost duration. Value must be between {min} — {max}!',
				values: { min: durationMin, max: durationMax },
			})}
			{...getNumberInputConfig(true)}
			icon={null}
			size="m"
			width="full"
			info={boostDurationInfo.info}
			error={boostDurationInfo.error}
			bind:value={boostDuration}
			id="input-blu-trv-boost-duration"
		/>
	</Container>

	<Container padding="s" type="borderSolid" backgroundColor="color-base">
		<Text styleType="h2" class="mb-s">
			{capFirstChar(
				$format('sub_settings_title_trv_boost_open_window_target_temperature', {
					default: 'Boost open window target temperature',
				}),
			)}
		</Text>
		<ToggleV2 class="mb-s" size="m" bind:checked={overrideEnable} id="toggle-blu-trv-override-enable">
			{$format('toggle_enable_temp_override', {
				default: 'Enable temperature override',
			})}
		</ToggleV2>
		<Text styleType="body-small" class="mb-s">
			{$format('text_trv_setting_temperature', { default: 'Temperature' })}
		</Text>
		<InputV2
			aria-label={$format('input_temp_override_open_window', {
				default: 'Enter the temperature override when window is open in °C',
			})}
			{...getNumberInputConfig(true)}
			icon={null}
			size="m"
			width="full"
			class="mb-s"
			info={overrideTargetInfo.info}
			error={overrideTargetInfo.error}
			bind:value={overrideTarget}
			id="input-blu-trv-override-target"
		/>
		<Text styleType="body-small" class="mb-s">
			{$format('text_trv_setting_duration', { default: 'Duration' })}
		</Text>
		<InputV2
			aria-label={$format('input_info_duration_temp_override', {
				default: 'Enter the duration of the temperature override in minutes',
			})}
			{...getNumberInputConfig(true)}
			icon={null}
			size="m"
			width="full"
			info={overrideDurationInfo.info}
			error={overrideDurationInfo.error}
			bind:value={overrideDuration}
			id="input-blu-trv-override-duration"
		/>
	</Container>
</div>

<style>
	.blu-trv-boost-settings {
		display: flex;
		flex-direction: column;
		gap: 12px;
		width: 100%;
	}
</style>
